import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";
import { QuizProvider } from "@/context/QuizContext";
import { EnquiryProvider } from "@/context/EnquiryContext";
import { EnquiryModal } from "@/components/site/EnquiryModal";
import { AuthProvider } from "@/context/AuthContext";
import { MemberProvider } from "@/context/MemberContext";
import { NexusProvider } from "@/context/NexusContext";
import { useLenis } from "@/hooks/useLenis";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { ChatWidget } from "@/components/site/ChatWidget";
import { QuizModal } from "@/components/site/QuizModal";
import { DesktopHint } from "@/components/site/DesktopHint";
import { RouteSeo } from "@/components/site/RouteSeo";
import { useTracking } from "@/hooks/useTracking";
import { usePixel } from "@/hooks/usePixel";
import { useFunnelPixel } from "@/hooks/useFunnelConfig";
import { GUIDE_INDEX, GUIDE_ZH_SLUGS } from "@/data/guideIndex";
import { ROUTE_LOADERS, prefetchAllRoutes } from "@/lib/routePrefetch";
import { useImagePlaceholders } from "@/hooks/useImagePlaceholders";

import Home from "@/pages/Home";
const About = lazy(ROUTE_LOADERS["/about"]);
const Platform = lazy(ROUTE_LOADERS["/platform"]);
const Events = lazy(ROUTE_LOADERS["/events"]);
const Media = lazy(ROUTE_LOADERS["/media"]);
const Services = lazy(ROUTE_LOADERS["/services"]);
const Careers = lazy(ROUTE_LOADERS["/careers"]);
const Go = lazy(() => import("@/pages/Go"));
const Team = lazy(ROUTE_LOADERS["/team"]);
const AdvisorProfile = lazy(ROUTE_LOADERS["/team/"]);
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const Legal = lazy(ROUTE_LOADERS["/legal"]);
const Guide = lazy(ROUTE_LOADERS["/guide"]);
const Article = lazy(ROUTE_LOADERS["/article"]);
const Guides = lazy(ROUTE_LOADERS["/guides"]);
const AdminLeads = lazy(() => import("@/pages/AdminLeads"));
const AdminMembers = lazy(() => import("@/pages/AdminMembers"));
const BeyondReferrals = lazy(ROUTE_LOADERS["/beyond-referrals"]);
const BeyondReferralsBook = lazy(() => import("@/pages/funnel/BeyondReferralsBook"));
const BeyondReferralsThanks = lazy(() => import("@/pages/funnel/BeyondReferralsThanks"));
const SurgeChrome = lazy(() => import("@/components/surge/SurgeChrome"));
const SurgeLogin = lazy(() => import("@/pages/surge/SurgeLogin"));
const SurgeDashboard = lazy(() => import("@/pages/surge/SurgeDashboard"));
const SurgeBrand = lazy(() => import("@/pages/surge/SurgeBrand"));
const SurgeIdeas = lazy(() => import("@/pages/surge/SurgeIdeas"));
const SurgeTrending = lazy(() => import("@/pages/surge/SurgeTrending"));
const SurgeCalendar = lazy(() => import("@/pages/surge/SurgeCalendar"));
const SurgeCreate = lazy(() => import("@/pages/surge/SurgeCreate"));
const SurgeGrowth = lazy(() => import("@/pages/surge/SurgeGrowth"));
const SurgeLibrary = lazy(() => import("@/pages/surge/SurgeLibrary"));
const SurgeSubmissions = lazy(() => import("@/pages/surge/SurgeSubmissions"));
const NexusChrome = lazy(() => import("@/components/nexus/NexusChrome"));
const NexusLogin = lazy(() => import("@/pages/nexus/NexusLogin"));
const NexusInstall = lazy(() => import("@/pages/nexus/NexusInstall"));
const NexusHome = lazy(() => import("@/pages/nexus/NexusHome"));
const NexusLeads = lazy(() => import("@/pages/nexus/NexusLeads"));
const NexusCalendar = lazy(() => import("@/pages/nexus/NexusCalendar"));
const NexusLibrary = lazy(() => import("@/pages/nexus/NexusLibrary"));
const NexusVideo = lazy(() => import("@/pages/nexus/NexusVideo"));
const NexusCourse = lazy(() => import("@/pages/nexus/NexusCourse"));
const NexusLesson = lazy(() => import("@/pages/nexus/NexusLesson"));
const NexusAsk = lazy(() => import("@/pages/nexus/NexusAsk"));
const NexusManage = lazy(() => import("@/pages/nexus/NexusManage"));
const NexusFieldwork = lazy(() => import("@/pages/nexus/NexusFieldwork"));
const NexusObjections = lazy(() => import("@/pages/nexus/NexusObjections"));
const NexusRoleplay = lazy(() => import("@/pages/nexus/NexusRoleplay"));
const NexusOnboarding = lazy(() => import("@/pages/nexus/NexusOnboarding"));
const NexusProgress = lazy(() => import("@/pages/nexus/NexusProgress"));
const NexusHelp = lazy(() => import("@/pages/nexus/NexusHelp"));
const NexusLiveSession = lazy(() => import("@/pages/nexus/NexusLiveSession"));
const NexusStartSession = lazy(() => import("@/pages/nexus/NexusLiveSession").then((m) => ({ default: m.StartSession })));
const NexusListenSession = lazy(() => import("@/pages/nexus/NexusListenSession"));
const NexusSessionDetail = lazy(() => import("@/pages/nexus/NexusSessionDetail"));

const PageFallback = () => <div style={{ minHeight: "60vh", background: "var(--navy)" }} aria-hidden />;

const SiteLayout = () => {
  useLenis();
  useTracking();
  usePixel();
  useEffect(() => { prefetchAllRoutes(); }, []);
  return (
    <>
      <RouteSeo />
      <Header />
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
      <Footer />
      <WhatsAppButton />
      <ChatWidget />
      <QuizModal />
      <EnquiryModal />
      <DesktopHint />
    </>
  );
};

const AdminLayout = () => {
  useLenis();
  return (
    <>
      <Helmet><title>Affinity Admin</title><meta name="robots" content="noindex,nofollow" /></Helmet>
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
    </>
  );
};

const CardLayout = () => {
  useLenis();
  useTracking();
  return (
    <>
      <RouteSeo />
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
      <EnquiryModal />
    </>
  );
};

const FunnelLayout = () => {
  useLenis();
  useTracking();
  usePixel();
  useFunnelPixel();
  return (
    <>
      <RouteSeo />
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
    </>
  );
};

const Shell = () => { useImagePlaceholders(); return null; };

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <Shell />
        <AuthProvider>
          <MemberProvider>
          <NexusProvider>
          <QuizProvider>
          <EnquiryProvider>
            <Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/team" element={<Team />} />
                <Route path="/team/:slug" element={<AdvisorProfile />} />
                <Route path="/services" element={<Services />} />
                <Route path="/platform" element={<Platform />} />
                <Route path="/stories" element={<Navigate to="/careers#stories" replace />} />
                <Route path="/media" element={<Media />} />
                <Route path="/events" element={<Events />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/go/:code" element={<Go />} />
                <Route path="/join" element={<Navigate to="/careers#conversation" replace />} />
                <Route path="/disclaimer" element={<Legal kind="disclaimer" />} />
                <Route path="/privacy-policy" element={<Legal kind="privacy" />} />
                <Route path="/guides" element={<Guides />} />
                <Route path="/articles/:slug" element={<Article />} />
                {GUIDE_INDEX.map((g) => <Route key={g.slug} path={`/${g.slug}`} element={<Guide />} />)}
                {GUIDE_ZH_SLUGS.map((slug) => <Route key={`zh-${slug}`} path={`/zh/${slug}`} element={<Guide />} />)}
              </Route>
              <Route element={<CardLayout />}>
                <Route path="/card/:slug" element={<AdvisorProfile focused />} />
              </Route>
              <Route element={<FunnelLayout />}>
                <Route path="/beyond-referrals" element={<BeyondReferrals />} />
                <Route path="/beyond-referrals/book" element={<BeyondReferralsBook />} />
                <Route path="/beyond-referrals/thank-you" element={<BeyondReferralsThanks />} />
              </Route>
              <Route element={<AdminLayout />}>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/leads" element={<AdminLeads />} />
                <Route path="/admin/members" element={<AdminMembers />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/links" element={<AdminDashboard />} />
                <Route path="/admin/enquiries" element={<AdminDashboard />} />
                <Route path="/admin/chats" element={<AdminDashboard />} />
                <Route path="/admin/booking" element={<AdminDashboard />} />
                <Route path="/admin/speakers" element={<AdminDashboard />} />
                <Route path="/admin/translations" element={<AdminDashboard />} />
                <Route path="/admin/funnel" element={<AdminDashboard />} />
              </Route>
              <Route path="/surge/login" element={<Suspense fallback={<PageFallback />}><SurgeLogin /></Suspense>} />
              <Route element={<SurgeChrome />}>
                <Route path="/surge" element={<SurgeDashboard />} />
                <Route path="/surge/brand" element={<SurgeBrand />} />
                <Route path="/surge/ideas" element={<SurgeIdeas />} />
                <Route path="/surge/trending" element={<SurgeTrending />} />
                <Route path="/surge/calendar" element={<SurgeCalendar />} />
                <Route path="/surge/create" element={<SurgeCreate />} />
                <Route path="/surge/growth" element={<SurgeGrowth />} />
                <Route path="/surge/library" element={<SurgeLibrary />} />
                <Route path="/surge/submissions" element={<SurgeSubmissions />} />
              </Route>
              <Route path="/nexus/login" element={<Suspense fallback={<PageFallback />}><NexusLogin /></Suspense>} />
              <Route path="/nexus/install" element={<Suspense fallback={<PageFallback />}><NexusInstall /></Suspense>} />
              <Route element={<Suspense fallback={<PageFallback />}><NexusChrome /></Suspense>}>
                <Route path="/nexus" element={<NexusHome />} />
                <Route path="/nexus/leads" element={<NexusLeads />} />
                <Route path="/nexus/calendar" element={<NexusCalendar />} />
                <Route path="/nexus/library" element={<NexusLibrary />} />
                <Route path="/nexus/library/:id" element={<NexusVideo />} />
                <Route path="/nexus/courses/:slug" element={<NexusCourse />} />
                <Route path="/nexus/courses/:slug/:lid" element={<NexusLesson />} />
                <Route path="/nexus/ask" element={<NexusAsk />} />
                <Route path="/nexus/manage" element={<NexusManage />} />
                <Route path="/nexus/fieldwork" element={<NexusFieldwork />} />
                <Route path="/nexus/objections" element={<NexusObjections />} />
                <Route path="/nexus/roleplay" element={<NexusRoleplay />} />
                <Route path="/nexus/onboarding" element={<NexusOnboarding />} />
                <Route path="/nexus/progress" element={<NexusProgress />} />
                <Route path="/nexus/help" element={<NexusHelp />} />
                <Route path="/nexus/fieldwork/start" element={<NexusStartSession />} />
                <Route path="/nexus/fieldwork/:id" element={<NexusSessionDetail />} />
                <Route path="/nexus/fieldwork/:id/live" element={<NexusLiveSession />} />
                <Route path="/nexus/fieldwork/:id/listen" element={<NexusListenSession />} />
              </Route>
            </Routes>
            <Toaster position="bottom-center" theme="dark" toastOptions={{ style: { background: "#0c1e22", color: "#f4f1ea", border: "1px solid rgba(212,168,77,.3)" } }} />
          </EnquiryProvider>
          </QuizProvider>
          </NexusProvider>
          </MemberProvider>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
